import Executive from '../../framework/rmi-util'
import { register } from '../../framework/rmi-server-util'
import { CheckApiKey, CheckPolicy, Resource } from '../../framework/permission-util'
import { RT } from '../permissions'
import { TicketOption2SalesChannelsAssignment } from './ticket-assignment.entities'
import { ticketAssignmentTechnical } from './TicketAssignmentTechnical'

/**
 * Ticket Assignment Executive
 * 
 * Handles high-level business operations for managing ticket option
 * assignments to sales channels and reseller categories.
 */
export default class TicketAssignmentExecutive extends Executive {

	initServer() {}

	/**
	 * Get or Create Ticket Option Assignment
	 * 
	 * Retrieves an existing assignment for a ticket option, or creates
	 * a new empty assignment if none exists. This supports the UI pattern
	 * where clicking a checkbox immediately creates an assignment object.
	 * 
	 * @param productId - Content Manager product ID
	 * @param clientId - Client ID from Content Manager
	 * @param serviceProviderId - Service Provider ID from Content Manager
	 * @param ticketOptionId - Ticket option ID within the product
	 * @returns Assignment with current sales channel and reseller category selections
	 */
	@CheckPolicy()
	async getOrCreateAssignment(
		productId: string,
		clientId: string,
		// only needed for permission checking
		@Resource(RT.PROVIDER) serviceProviderId: string,
		ticketOptionId: string
	): Promise<TicketOption2SalesChannelsAssignment> {
		return await ticketAssignmentTechnical.getOrCreateAssignment(productId, clientId, ticketOptionId)
	}

	/**
	 * Get Product Assignments (User Access)
	 * 
	 * Same as getProductAssignments but with user-based permission checking
	 * for internal dashboard/admin access.
	 */
	@CheckPolicy()
	async getProductAssignments(
		productId: string,
		@Resource(RT.CLIENT) clientId: string,
		// only needed for permission checking
		@Resource(RT.PROVIDER) serviceProviderId: string
	): Promise<TicketOption2SalesChannelsAssignment[]> {
		return await ticketAssignmentTechnical.getAssignmentsByProductId(productId, clientId)
	}

	/**
	 * Get Product Assignments (User Access)
	 * 
	 * Same as getProductAssignments but with user-based permission checking
	 * for internal dashboard/admin access.
	 */
	@CheckPolicy()
	async getProductAssignmentsForAllClients(
		productId: string,
		// only needed for permission checking
		@Resource(RT.CLIENT) clientId: string,
		@Resource(RT.PROVIDER) serviceProviderId: string
	): Promise<TicketOption2SalesChannelsAssignment[]> {
		return await ticketAssignmentTechnical.getProductAssignmentsForAllClients(productId)
	}

	/**
	 * Remove Custom Assignment
	 * 
	 * Deletes a custom assignment, reverting the ticket option back to
	 * using the default client-level sales channel configuration.
	 * This is called when the user unchecks the "special assignment" checkbox?!
	 * 
	 * @param productId - Content Manager product ID
	 * @param clientId - Client ID from Content Manager
	 * @param ticketOptionId - Ticket option ID to remove assignment for
	 */
	@CheckPolicy()
	async removeAssignment(
		productId: string,
		@Resource(RT.CLIENT) clientId: string,
		@Resource(RT.PROVIDER) serviceProviderId: string,
		ticketOptionId: string
	): Promise<void> {
		await ticketAssignmentTechnical.deleteAssignment(productId, clientId, ticketOptionId)
	}

	/**
	 * Update Assignment (User Access)
	 * 
	 * Updates a specific assignment with user-based permission checking.
	 * This constructs the proper resource ID for permission validation.
	 */
	@CheckPolicy()
	async updateAssignment(
		productId: string,
		@Resource(RT.CLIENT) clientId: string,
		@Resource(RT.PROVIDER) serviceProviderId: string,
		ticketOptionId: string,
		salesChannelIds: string[],
		resellerCategoryIds: string[]
	): Promise<TicketOption2SalesChannelsAssignment> {

		return await ticketAssignmentTechnical.updateAssignment(
			productId,
			clientId,
			ticketOptionId,
			salesChannelIds,
			resellerCategoryIds
		)
	}

	// =================================================================
	// EXTERNAL API - can be invoked by Postman or third party systems with a MYS_API_KEY
	// =================================================================

	/**
	 * Update Sales Channel Assignment
	 * 
	 * Updates the sales channels and reseller categories for a ticket option.
	 * This is called when the user makes changes in the assignment dialog
	 * and clicks "Save".
	 * 
	 * @param productId - Content Manager product ID
	 * @param clientId - Client ID from Content Manager
	 * @param ticketOptionId - Ticket option ID to update
	 * @param salesChannelIds - Array of selected sales channel IDs
	 * @param resellerCategoryIds - Array of selected reseller category IDs
	 * @returns Updated assignment
	 */
	@CheckApiKey()
	async updateAssignmentExternal(
		productId: string,
		@Resource(RT.CLIENT) clientId: string,
		ticketOptionId: string,
		salesChannelIds: string[],
		resellerCategoryIds: string[]
	): Promise<TicketOption2SalesChannelsAssignment> {
		return await ticketAssignmentTechnical.updateAssignment(
			productId,
			clientId,
			ticketOptionId,
			salesChannelIds,
			resellerCategoryIds
		)
	}

	/**
	 * Get Product Assignments
	 * 
	 * Retrieves all custom assignments for ticket options within a product.
	 * This is used when loading the ticket section of a product to show
	 * which ticket options have custom channel assignments.
	 * 
	 * @param productId - Content Manager product ID
	 * @param clientId - Client ID from Content Manager
	 * @returns Array of assignments for the product
	 */
	@CheckApiKey()
	async getProductAssignmentsExternal(
		productId: string,
		clientId: string
	): Promise<TicketOption2SalesChannelsAssignment[]> {
		return await ticketAssignmentTechnical.getAssignmentsByProductId(productId, clientId)
	}
	
	/**
	 * Bulk Upsert Assignments
	 * 
	 * Creates or updates multiple assignments in a single operation.
	 * This can be useful for bulk operations or imports.
	 * 
	 * @param assignments - Array of assignments to upsert
	 * @returns Array of upserted assignments
	 */
	@CheckApiKey()
	async upsertAssignments(
		assignments: TicketOption2SalesChannelsAssignment[]
	): Promise<TicketOption2SalesChannelsAssignment[]> {
		const results: TicketOption2SalesChannelsAssignment[] = []
		
		for (const assignment of assignments) {
			const result = await ticketAssignmentTechnical.upsertAssignment(assignment)
			results.push(result)
		}
		
		return results
	}

	/**
	 * Find Assignments by Sales Channel
	 * 
	 * Finds all ticket options that are assigned to a specific sales channel.
	 * This can be useful for impact analysis when making changes to sales channels.
	 * 
	 * @param salesChannelId - Sales channel ID to search for
	 * @param clientId - Client ID from Content Manager
	 * @returns Array of assignments using this sales channel
	 */
	@CheckApiKey()
	async getAssignmentsBySalesChannel(
		salesChannelId: string,
		clientId: string
	): Promise<TicketOption2SalesChannelsAssignment[]> {
		return await ticketAssignmentTechnical.getAssignmentsBySalesChannel(salesChannelId, clientId)
	}

	/**
	 * Find Assignments by Reseller Category
	 * 
	 * Finds all ticket options that are assigned to a specific reseller category.
	 * This can be useful for impact analysis when making changes to reseller categories.
	 * 
	 * @param resellerCategoryId - Reseller category ID to search for
	 * @param clientId - Client ID from Content Manager
	 * @returns Array of assignments using this reseller category
	 */
	@CheckApiKey()
	async getAssignmentsByResellerCategory(
		resellerCategoryId: string,
		clientId: string
	): Promise<TicketOption2SalesChannelsAssignment[]> {
		return await ticketAssignmentTechnical.getAssignmentsByResellerCategory(resellerCategoryId, clientId)
	}

	/**
	 * Get Assignment Details
	 * 
	 * Retrieves a specific assignment by its composite key. Used when editing
	 * an existing assignment.
	 * 
	 * @param productId - Content Manager product ID
	 * @param clientId - Client ID from Content Manager  
	 * @param ticketOptionId - Ticket option ID
	 * @returns Assignment details or null if not found
	 */
	@CheckApiKey()
	async getAssignment(
		productId: string,
		clientId: string,
		ticketOptionId: string
	): Promise<TicketOption2SalesChannelsAssignment | null> {
		return await ticketAssignmentTechnical.getAssignmentByKey(productId, clientId, ticketOptionId)
	}

	/**
	 * Get Product Assignments (User Access)
	 * 
	 * Same as getProductAssignments but with user-based permission checking
	 * for internal dashboard/admin access.
	 */
	@CheckApiKey()
	async getProductAssignmentsForAllClientsExternal(
		productId: string
	): Promise<TicketOption2SalesChannelsAssignment[]> {
		return await ticketAssignmentTechnical.getProductAssignmentsForAllClients(productId)
	}

	/**
	 * Persist the ticket option assignments
	 * 
	 * Persist the ticket option assignments with user-based permission checking
	 * for internal dashboard/admin access.
	 */
	@CheckApiKey()
	async persistTicketOptionAssignmentsExternal(
		productId: string,
		ticketOptions,
		ticketOptionAssignments
	): Promise<TicketOption2SalesChannelsAssignment[]> {
		return await ticketAssignmentTechnical.persistTicketOptionAssignments(productId, ticketOptions, ticketOptionAssignments)
	}

}

register(TicketAssignmentExecutive)
